/**
 * Rapprochement « déclaré vs observé » (CPL-03/04).
 *
 * Le questionnaire AI Readiness dit ce que le client **croit** avoir : ses
 * machines, ses GPU, ses fournisseurs cloud, ses sources de données. L'audit
 * dit ce que la collecte a **réellement trouvé**. L'écart entre les deux est le
 * livrable : une source déclarée mais jamais vue est soit éteinte, soit hors
 * périmètre, soit inaccessible au compte de service ; une source vue mais non
 * déclarée est une donnée que personne ne suit.
 *
 * Trois états, jamais confondus :
 *
 * * `match` — déclaré ET observé ;
 * * `declared_only` / `observed_only` — un écart exploitable, avec de quoi agir ;
 * * `not_measured` — l'audit n'a PAS regardé (module absent, catalogue absent).
 *   Ce n'est pas un écart : présenter « rien trouvé » quand rien n'a été
 *   cherché serait un mensonge, et c'est exactement ce que ce module refuse.
 *
 * Le questionnaire est stocké tel que le formulaire embarqué l'émet : un
 * instantané `FormData` à plat, dont les tableaux sont indexés
 * (`hosts[0].hostname`, `data_sources[3].endpoint`). Toute clé absente est
 * tolérée : un brouillon à moitié rempli se rapproche quand même.
 */

// ─── Lecture tolérante de l'instantané du questionnaire ──────────────────

type Flat = Record<string, unknown>;

const isObj = (v: unknown): v is Flat => !!v && typeof v === 'object' && !Array.isArray(v);

/** Une valeur du formulaire : chaîne simple, ou première valeur d'une case à cocher multiple. */
const one = (v: unknown): string | null => {
  if (typeof v === 'string') return v.trim() === '' ? null : v.trim();
  if (Array.isArray(v)) {
    for (const x of v) { const s = one(x); if (s) return s; }
  }
  return null;
};

/** Une valeur multiple (`infra.cloud_fournisseurs` coché plusieurs fois). */
const many = (v: unknown): string[] => {
  const list = Array.isArray(v) ? v : [v];
  return [...new Set(list.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map(x => x.trim()))];
};

const int = (v: unknown): number | null => {
  const s = one(v);
  if (!s) return null;
  const n = Number.parseInt(s.replace(/[^\d-]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * `hosts[0].hostname` → lignes ordonnées `{hostname: …}`.
 * Les index sont ceux du formulaire : une ligne supprimée laisse un trou, qui
 * est simplement ignoré.
 */
function indexedRows(data: Flat, prefix: string): Record<string, string>[] {
  const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\[(\\d+)\\]\\.(.+)$`);
  const byIndex = new Map<number, Record<string, string>>();
  for (const [key, raw] of Object.entries(data)) {
    const m = re.exec(key);
    const value = one(raw);
    if (!m || !value) continue;
    const idx = Number.parseInt(m[1], 10);
    const row = byIndex.get(idx) ?? {};
    row[m[2]] = value;
    byIndex.set(idx, row);
  }
  return [...byIndex.entries()].sort((a, b) => a[0] - b[0]).map(([, row]) => row);
}

/** Minuscules, accents retirés : « SRV-Fichiers.corp » et « srv-fichiers.CORP » sont la même machine. */
function norm(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** `srv-fs01.corp.example.com` → `srv-fs01` (comparer des noms courts avec des FQDN). */
const shortName = (value: string): string => norm(value).split(/[.\\/]/)[0].replace(/\$$/, '');

// ─── Ce que le client a déclaré ──────────────────────────────────────────

/** Réponse à « les données peuvent-elles sortir ? » (`scan.data_can_leave`, texte libre). */
type EgressStance = 'yes' | 'no' | 'conditional' | 'unknown';

interface DeclaredHost { name: string; os: string | null; role: string | null; scanAllowed: string | null }
interface DeclaredSource { name: string; type: string | null; tech: string | null; endpoint: string | null }

export interface Declared {
  hosts: DeclaredHost[];
  /** `compute.gpu.disponible` : `oui` | `non` | `partages` | `cloud_a_la_demande`. */
  gpuStance: string | null;
  gpuCount: number | null;
  gpuModels: string[];
  cloudProviders: string[];
  sources: DeclaredSource[];
  egress: EgressStance;
  /** Le texte exact saisi par le client : il est TOUJOURS montré tel quel. */
  egressRaw: string | null;
  redactionRequired: boolean | null;
}

/** « Aucun fournisseur » est une réponse, pas un fournisseur. */
const NO_PROVIDER = 'aucun fournisseur';

/**
 * `scan.data_can_leave` est un champ libre (« Oui / Non / Sous conditions… »).
 * On ne conclut « oui » ou « non » que sur une réponse franche ; tout le reste
 * reste « sous conditions », et le texte brut accompagne toujours le verdict.
 */
export function egressStance(raw: string | null): EgressStance {
  if (!raw) return 'unknown';
  const v = norm(raw);
  if (/^(non|no)\b/.test(v) || /jamais|interdit|aucune sortie/.test(v)) return 'no';
  if (/^(oui|yes)\b/.test(v) || /sans restriction|aucune restriction/.test(v)) return 'yes';
  return 'conditional';
}

/**
 * Instantané du questionnaire → ce que le client a déclaré. `null` si l'objet
 * ne porte aucune des clés attendues (questionnaire vide ou d'un autre modèle).
 */
export function parseDeclared(data: unknown): Declared | null {
  if (!isObj(data)) return null;
  const keys = Object.keys(data).filter(k => k !== '_meta');
  if (keys.length === 0) return null;

  const hosts = indexedRows(data, 'hosts')
    .filter(r => !!r.hostname)
    .map(r => ({
      name: r.hostname, os: r.os ?? null, role: r.role ?? null,
      scanAllowed: r.scan_allowed ?? null,
    }));
  const sources = indexedRows(data, 'data_sources')
    .filter(r => !!r.name)
    .map(r => ({ name: r.name, type: r.type ?? null, tech: r.tech ?? null, endpoint: r.endpoint ?? null }));
  const egressRaw = one(data['scan.data_can_leave']);
  const redaction = one(data['scan.redaction_required']);

  return {
    hosts,
    gpuStance: one(data['compute.gpu.disponible']),
    gpuCount: int(data['compute.agrege.nb_gpu']),
    gpuModels: indexedRows(data, 'compute.gpu.details').map(r => r.modele).filter(Boolean),
    cloudProviders: many(data['infra.cloud_fournisseurs']).filter(p => norm(p) !== NO_PROVIDER),
    sources,
    egress: egressStance(egressRaw),
    egressRaw,
    redactionRequired: redaction === null ? null : norm(redaction) === 'oui',
  };
}

// ─── Ce que l'audit a observé ────────────────────────────────────────────

interface ObservedSource { label: string; locator: string | null }

export interface Observed {
  /** Toutes les machines nommées par la collecte (locale et distante). */
  hosts: string[];
  gpuCount: number | null;
  gpuModel: string | null;
  /** `null` = le catalogue 1.2 est absent : le cloud n'a pas été cherché. */
  cloudProviders: string[] | null;
  /** `null` = le catalogue 1.2 est absent : les sources n'ont pas été cataloguées. */
  sources: ObservedSource[] | null;
}

/**
 * Empreintes reconnues d'un fournisseur cloud dans un locator. Volontairement
 * étroites : `oracle` seul est un moteur de base, pas Oracle Cloud — seul
 * `oraclecloud` / `oci.` compte.
 */
const CLOUD_SIGNATURES: [provider: string, pattern: RegExp][] = [
  ['aws',      /\b(aws|amazonaws)\b|\bs3:\/\/|s3[.-]|\.amazonaws\./],
  ['azure',    /\bazure\b|\.core\.windows\.net|blob\.core\./],
  ['gcp',      /\bgcp\b|googleapis|google(cloud|usercontent)|\bgs:\/\//],
  ['ovh',      /\bovh(cloud)?\b/],
  ['scaleway', /\bscaleway\b|scw\.cloud/],
  ['ibm',      /\bibmcloud\b|cloud\.ibm\.com/],
  ['oracle',   /\boraclecloud\b|\boci\.|oraclecloud\.com/],
];

/** Fournisseurs cloud reconnaissables dans une liste de locators / hôtes. */
export function cloudProvidersIn(haystack: string[]): string[] {
  const blob = haystack.map(norm).join(' ');
  return CLOUD_SIGNATURES.filter(([, re]) => re.test(blob)).map(([provider]) => provider);
}

/**
 * Entrées de la colonne « observé », décrites structurellement pour que ce
 * module reste indépendant du catalogue et de l'inventaire distant (et donc
 * testable sans eux).
 *
 * `catalogSources` à `null` = l'audit ne porte pas de catalogue 1.2 : le cloud
 * et les sources n'ont PAS été cherchés, et les sujets correspondants sont
 * « non mesuré » plutôt que « rien trouvé ».
 */
interface ObservedInputs {
  systemName?: string | null;
  gpuCount?: number | null;
  gpuModel?: string | null;
  catalogSources: { label: string; locator: string | null; host: string | null }[] | null;
  /** Machines nommées par le module 09 ; `[]` sur un audit d'une seule machine. */
  fleetHosts: string[];
}

/** Assemble la colonne « observé » : machine auditée + parc distant + catalogue. */
export function observedFrom(input: ObservedInputs): Observed {
  const catalog = input.catalogSources;
  const hosts = [
    input.systemName ?? null,
    ...input.fleetHosts,
    ...(catalog ?? []).map(s => s.host),
  ].filter((h): h is string => !!h && h !== '—');
  return {
    hosts: [...new Set(hosts)],
    gpuCount: input.gpuCount ?? null,
    gpuModel: input.gpuModel ?? null,
    cloudProviders: catalog
      ? cloudProvidersIn(catalog.flatMap(s => [s.locator, s.host, s.label].filter((x): x is string => !!x)))
      : null,
    sources: catalog ? catalog.map(s => ({ label: s.label, locator: s.locator })) : null,
  };
}

// ─── Le rapprochement ────────────────────────────────────────────────────

export type MatchState = 'match' | 'declared_only' | 'observed_only' | 'not_measured';
export type ReconTopicKey = 'hosts' | 'gpu' | 'cloud' | 'sources';

interface ReconRow {
  key: string;
  declared: string | null;
  observed: string | null;
  state: MatchState;
  /** Complément affiché sous la ligne (rôle déclaré, moteur observé…). */
  detail: string | null;
}

interface ReconTopic {
  key: ReconTopicKey;
  rows: ReconRow[];
  /** `false` = l'audit n'a pas regardé ce sujet : aucune ligne n'est un écart. */
  measured: boolean;
  gaps: number;
}

interface Reconciliation {
  topics: ReconTopic[];
  gaps: number;
  egress: EgressStance;
  egressRaw: string | null;
  redactionRequired: boolean | null;
}

const GAP_STATES: MatchState[] = ['declared_only', 'observed_only'];
const countGaps = (rows: ReconRow[]): number => rows.filter(r => GAP_STATES.includes(r.state)).length;

function topic(key: ReconTopicKey, rows: ReconRow[], measured: boolean): ReconTopic {
  return { key, rows, measured, gaps: measured ? countGaps(rows) : 0 };
}

function hostRows(declared: DeclaredHost[], observed: string[]): ReconRow[] {
  const observedShort = new Map<string, string>();
  for (const name of observed) {
    const key = shortName(name);
    // Le nom le plus long l'emporte : un FQDN est plus informatif qu'un nom court.
    if (!observedShort.has(key) || name.length > observedShort.get(key)!.length) observedShort.set(key, name);
  }
  const matched = new Set<string>();
  const rows: ReconRow[] = declared.map(h => {
    const key = shortName(h.name);
    const hit = observedShort.get(key);
    if (hit) matched.add(key);
    return {
      key: `host:${key}`,
      declared: h.name,
      observed: hit ?? null,
      state: hit ? 'match' as const : 'declared_only' as const,
      detail: [h.role, h.os].filter(Boolean).join(' · ') || null,
    };
  });
  for (const [key, name] of observedShort) {
    if (matched.has(key)) continue;
    rows.push({ key: `host:${key}`, declared: null, observed: name, state: 'observed_only', detail: null });
  }
  return rows;
}

/** Un GPU déclaré « sur la machine » : `cloud_a_la_demande` n'en est pas un. */
const LOCAL_GPU_STANCES = new Set(['oui', 'partages']);

function gpuRow(d: Declared, o: Observed): ReconRow {
  const declaredParts = [
    d.gpuStance,
    d.gpuCount != null ? `${d.gpuCount}` : null,
    d.gpuModels.join(', ') || null,
  ].filter(Boolean) as string[];
  const observedParts = [
    o.gpuCount != null ? `${o.gpuCount}` : null,
    o.gpuModel,
  ].filter(Boolean) as string[];
  const row = {
    key: 'gpu',
    declared: declaredParts.join(' · ') || null,
    observed: observedParts.join(' · ') || null,
    detail: null,
  };
  if (o.gpuCount == null) return { ...row, state: 'not_measured' };
  const declaredKnown = d.gpuStance != null || d.gpuCount != null;
  const declaredHas = LOCAL_GPU_STANCES.has(norm(d.gpuStance ?? '')) || (d.gpuCount ?? 0) > 0;
  const observedHas = o.gpuCount > 0;
  if (!declaredKnown) return { ...row, state: observedHas ? 'observed_only' : 'not_measured' };
  if (declaredHas === observedHas) return { ...row, state: 'match' };
  return { ...row, state: declaredHas ? 'declared_only' : 'observed_only' };
}

function cloudRows(declared: string[], observed: string[]): ReconRow[] {
  const seen = new Set(observed.map(norm));
  const rows: ReconRow[] = declared.map(p => ({
    key: `cloud:${norm(p)}`,
    declared: p,
    observed: seen.has(norm(p)) ? p : null,
    state: seen.has(norm(p)) ? 'match' as const : 'declared_only' as const,
    detail: null,
  }));
  const declaredSet = new Set(declared.map(norm));
  for (const p of observed) {
    if (declaredSet.has(norm(p))) continue;
    rows.push({ key: `cloud:${norm(p)}`, declared: null, observed: p, state: 'observed_only', detail: null });
  }
  return rows;
}

/** Jetons significatifs d'une source déclarée : nom, technologie, hôte de l'endpoint. */
function declaredTokens(s: DeclaredSource): string[] {
  const raw = [s.name, s.tech, s.endpoint?.split(/[/:@]/).filter(Boolean).pop() ?? null];
  return [...new Set(raw
    .filter((x): x is string => !!x)
    .flatMap(x => norm(x).split(/[^a-z0-9]+/))
    .filter(tok => tok.length >= 3))];
}

function sourceRows(declared: DeclaredSource[], observed: ObservedSource[]): ReconRow[] {
  const blobs = observed.map(o => norm([o.label, o.locator].filter(Boolean).join(' ')));
  const matched = new Set<number>();
  const rows: ReconRow[] = declared.map((s, i) => {
    const tokens = declaredTokens(s);
    const hit = tokens.length === 0 ? -1 : blobs.findIndex(b => tokens.some(tok => b.includes(tok)));
    if (hit >= 0) matched.add(hit);
    return {
      key: `src:d${i}`,
      declared: s.name,
      observed: hit >= 0 ? (observed[hit].locator ?? observed[hit].label) : null,
      state: hit >= 0 ? 'match' as const : 'declared_only' as const,
      detail: [s.type, s.tech].filter(Boolean).join(' · ') || null,
    };
  });
  observed.forEach((o, i) => {
    if (matched.has(i)) return;
    rows.push({
      key: `src:o${i}`,
      declared: null,
      observed: o.locator ?? o.label,
      state: 'observed_only',
      detail: o.locator ? o.label : null,
    });
  });
  return rows;
}

/**
 * Confronte le questionnaire à la collecte. `null` quand aucun questionnaire
 * soumis n'est rattaché à l'audit : il n'y a alors rien à rapprocher, et
 * l'onglet le dit au lieu d'afficher un tableau de « non déclaré ».
 */
export function reconcile(declared: Declared | null, observed: Observed): Reconciliation | null {
  if (!declared) return null;
  const topics: ReconTopic[] = [
    topic('hosts', hostRows(declared.hosts, observed.hosts), observed.hosts.length > 0),
    topic('gpu', [gpuRow(declared, observed)], observed.gpuCount != null),
    topic('cloud', cloudRows(declared.cloudProviders, observed.cloudProviders ?? []),
      observed.cloudProviders !== null),
    topic('sources', sourceRows(declared.sources, observed.sources ?? []), observed.sources !== null),
  ];
  return {
    topics,
    gaps: topics.reduce((n, tp) => n + tp.gaps, 0),
    egress: declared.egress,
    egressRaw: declared.egressRaw,
    redactionRequired: declared.redactionRequired,
  };
}
